import { createSlice } from "@reduxjs/toolkit";
import {
  getFeatureFlags,
  createOrUpdateFeatureFlags,
  deleteFeatureFlags,
  getTicketOrCaseFeatureFlagEnabled,
} from "../services/featureFlags";
import { CURRENT_TICKET_OR_CASE_NUMBER } from "../constants";

export interface FeatureFlag {
  name: string;
  enabled: boolean;
  [key: string]: unknown;
}

interface FeatureFlagsApiResponse {
  data: {
    featureFlags: FeatureFlag[];
  };
}

interface FeatureFlagsState {
  status: string;
  ticketOrCaseFeatureFlagEnabled: boolean;
  featureFlags: FeatureFlag[];
}

const initialState: FeatureFlagsState = {
  status: "idle",
  ticketOrCaseFeatureFlagEnabled: false,
  featureFlags: [],
};

const getFeatureFlagsDataFromPayload = (
  payload: FeatureFlag[],
): FeatureFlag[] => {
  return (
    payload &&
    payload.reduce<FeatureFlag[]>((acc, cur) => {
      acc.push({
        ...cur,
      });
      return acc;
    }, [])
  );
};

const featureFlagsSlice = createSlice({
  name: "featureFlag",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getFeatureFlags.pending, (state) => {
        state.status = "pending";
      })
      .addCase(getFeatureFlags.fulfilled, (state, action) => {
        const payload = action.payload as FeatureFlagsApiResponse | undefined;
        const featureFlags = payload?.data?.featureFlags ?? [];
        const ticketOrCaseFeatureFlag = featureFlags.find(
          (flag) => flag.name === CURRENT_TICKET_OR_CASE_NUMBER,
        );
        state.status = "success";
        state.featureFlags = getFeatureFlagsDataFromPayload(featureFlags);
        state.ticketOrCaseFeatureFlagEnabled =
          ticketOrCaseFeatureFlag &&
          Object.keys(ticketOrCaseFeatureFlag).length > 0
            ? ticketOrCaseFeatureFlag.enabled
            : false;
      })
      .addCase(getFeatureFlags.rejected, (state) => {
        state.status = "rejected";
        state.ticketOrCaseFeatureFlagEnabled = false;
      })
      .addCase(createOrUpdateFeatureFlags.fulfilled, (state, action) => {
        const payload = action.payload as FeatureFlagsApiResponse | undefined;
        const featureFlags = payload?.data?.featureFlags ?? [];
        state.featureFlags = getFeatureFlagsDataFromPayload(featureFlags);
        state.status = "success";
      })
      .addCase(createOrUpdateFeatureFlags.rejected, (state) => {
        state.status = "rejected";
      })
      .addCase(deleteFeatureFlags.fulfilled, (state, action) => {
        const payload = action.payload as FeatureFlagsApiResponse | undefined;
        const featureFlags = payload?.data?.featureFlags ?? [];
        state.featureFlags = getFeatureFlagsDataFromPayload(featureFlags);
        state.status = "success";
      })
      .addCase(deleteFeatureFlags.rejected, (state) => {
        state.status = "rejected";
      })
      .addCase(getTicketOrCaseFeatureFlagEnabled, (state, action) => {
        state.ticketOrCaseFeatureFlagEnabled =
          action.payload as unknown as boolean;
      });
  },
});

export default featureFlagsSlice.reducer;
